// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import { useMsal } from '../../../../recruiter/useMsal';
import { Sidebar } from '../../../../components/Sidebar';
import styles from './candidates.module.scss';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PageLoader from '../../../../components/PageLoader';
import {
  fetchJobCandidates,
  Candidate,
  sendInterviewLink,
  InterviewLinkResponse,
  handleInvitationAction,
  exportCandidates,
  exportInterviewReport,
  runSkillAssessment,
  SkillAssessmentLinkResponse,
  AssessmentDeliveryChannel
} from '../../../../store/interviewHostSlice';
import ConfigureAssessmentModal from './ConfigureAssessmentModal';
import RejectCandidateModal from './RejectCandidateModal';
import InterviewLinkModal from './InterviewLinkModal';
import SkillAssessmentLinkModal from './SkillAssessmentLinkModal';
import ConfirmCancelModal from './ConfirmCancelModal';
import {
  queuePendingSkillAssessment,
  getPendingSkillAssessment,
  clearPendingSkillAssessment
} from './pendingSkillAssessments';

type StatusFilter = 'All' | 'ScreeningInProgress' | 'ScreeningCompleted' | 'Invited' | 'ReportReady' | 'Decided';

const Candidates: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { candidates, candidatesLoading, candidatesError } = useSelector((state: RootState) => state.interviewHost);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [activeStatus, setActiveStatus] = useState<StatusFilter>((searchParams.get('status') as StatusFilter) || 'All');
  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});
  const [configureAssessmentModalOpen, setConfigureAssessmentModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [interviewLinkModalOpen, setInterviewLinkModalOpen] = useState(false);
  const [selectedCandidateForReject, setSelectedCandidateForReject] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedCandidateForConfig, setSelectedCandidateForConfig] = useState<string | null>(null);
  const [interviewLinkData, setInterviewLinkData] = useState<InterviewLinkResponse | null>(null);
  const [sendingInterview, setSendingInterview] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    open: false,
    message: '',
    type: 'success'
  });
  const [confirmCancelModal, setConfirmCancelModal] = useState<{
    open: boolean;
    candidateId: string;
    candidateName: string;
  }>({
    open: false,
    candidateId: '',
    candidateName: ''
  });
  const [cancellingInvite, setCancellingInvite] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const [skillAssessmentLinkModalOpen, setSkillAssessmentLinkModalOpen] = useState(false);
  const [skillAssessmentLinkData, setSkillAssessmentLinkData] = useState<{
    data: SkillAssessmentLinkResponse;
    candidateName: string;
  } | null>(null);
  const [runningSkillAssessment, setRunningSkillAssessment] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch candidates with current filters
  const fetchCandidatesWithFilters = useCallback(() => {
    if (jobId) {
      const search = searchParams.get('search') || undefined;
      const status = searchParams.get('status') || undefined;
      dispatch(fetchJobCandidates({ jobId, search, status }));
    }
  }, [jobId, searchParams, dispatch]);

  // Initial fetch and refetch when URL params change
  useEffect(() => {
    fetchCandidatesWithFilters();
  }, [fetchCandidatesWithFilters]);

  // Linked from a dashboard popup (?candidate=<id>): expand that candidate once the list loads.
  const focusCandidateId = searchParams.get('candidate');
  const focusedCandidateHandled = useRef<string | null>(null);
  useEffect(() => {
    if (!focusCandidateId || focusedCandidateHandled.current === focusCandidateId) return;
    const match = candidates?.candidates?.find((c) => c.candidate_id === focusCandidateId);
    if (!match) return;
    focusedCandidateHandled.current = focusCandidateId;
    setExpandedCandidateId(match.candidate_id);
  }, [focusCandidateId, candidates]);

  const handleLogout = useCallback(async () => {
    try {
      // logoutPopup already performs the full-page redirect to /api/sso/logout;
      // navigating again here would cancel that request before the server can
      // destroy the session and clear the cookie.
      await instance.logoutPopup({
        postLogoutRedirectUri: '/login'
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  const handleBackToJobs = useCallback(() => {
    navigate('/jobs');
  }, [navigate]);

  const handleBackToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Handle search input with debouncing
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer for debounced search
      debounceTimerRef.current = setTimeout(() => {
        const newParams = new URLSearchParams(searchParams);
        if (value.trim()) {
          newParams.set('search', value.trim());
        } else {
          newParams.delete('search');
        }
        setSearchParams(newParams);
      }, 500); // 500ms debounce delay
    },
    [searchParams, setSearchParams]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setSearchInput('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle status filter change
  const handleStatusChange = useCallback(
    (status: StatusFilter) => {
      setActiveStatus(status);
      const newParams = new URLSearchParams(searchParams);

      if (status === 'All') {
        newParams.delete('status');
      } else {
        newParams.set('status', status);
      }

      setSearchParams(newParams);
      // Reset expanded state when changing filters
      setExpandedCandidateId(null);
    },
    [searchParams, setSearchParams]
  );

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Reset state
    setSearchInput('');
    setActiveStatus('All');

    // Clear URL params
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const toggleCandidateExpanded = useCallback((candidateId: string, candidateStatus: string) => {
    // Only allow expansion for ScreeningCompleted, Invited, ReportReady, and Decided statuses
    const expandableStatuses = ['ScreeningCompleted', 'Invited', 'ReportReady', 'Decided'];
    if (expandableStatuses.includes(candidateStatus)) {
      setExpandedCandidateId((prev) => (prev === candidateId ? null : candidateId));
    }
  }, []);

  const toggleAccordion = useCallback((candidateId: string, section: string) => {
    const key = `${candidateId}_${section}`;
    setExpandedAccordions((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const handleRejectClick = useCallback((candidateId: string, candidateName: string) => {
    setSelectedCandidateForReject({ id: candidateId, name: candidateName });
    setRejectModalOpen(true);
  }, []);

  const handleRejectModalClose = useCallback(() => {
    setRejectModalOpen(false);
    setSelectedCandidateForReject(null);
  }, []);

  const handleConfigureClick = useCallback((candidateId: string) => {
    setSelectedCandidateForConfig(candidateId);
    setConfigureAssessmentModalOpen(true);
  }, []);

  const handleConfigModalClose = useCallback(() => {
    setConfigureAssessmentModalOpen(false);
    setSelectedCandidateForConfig(null);
  }, []);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ open: true, message, type });
    setTimeout(() => {
      setToast({ open: false, message: '', type: 'success' });
    }, 4000);
  }, []);

  // Show a one-time toast when we've just been redirected here after adding
  // candidate(s), e.g. from the Add Candidate page. Clear the state afterwards
  // so refreshing or navigating back doesn't re-show it.
  useEffect(() => {
    const flashMessage = (location.state as { flashMessage?: string } | null)?.flashMessage;
    if (flashMessage) {
      showToast(flashMessage);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state]);

  const handleSendInterviewLink = useCallback(
    async (candidate: Candidate) => {
      if (!jobId) return;

      setSendingInterview(true);
      try {
        const deliveryChannels = candidate.interview_configuration?.delivery_channels?.length
          ? candidate.interview_configuration.delivery_channels
          : (['interview_host'] as AssessmentDeliveryChannel[]);
        const sendInterviewHost = deliveryChannels.includes('interview_host');
        const sendSkillAssessment = deliveryChannels.includes('ai_skill_assessment');

        let candidateName = candidate.name;

        // Send Interview Host assessment
        if (sendInterviewHost) {
          const result = await dispatch(sendInterviewLink({ candidateId: candidate.candidate_id, jobId })).unwrap();
          candidateName = result.recruiter_context.candidate_name || candidate.name;
          setInterviewLinkData(result);
          setInterviewLinkModalOpen(true);

          // Queue skill assessment to be sent automatically after interview completion
          if (sendSkillAssessment) {
            queuePendingSkillAssessment(candidate.candidate_id, {
              jobId,
              candidateName,
              deadlineHours: 48
            });
          }
        }

        // Send Skill Assessment immediately (only if not waiting for Interview Host)
        if (sendSkillAssessment && !sendInterviewHost) {
          const result = await dispatch(runSkillAssessment({ candidateId: candidate.candidate_id, jobId })).unwrap();
          setSkillAssessmentLinkData({ data: result, candidateName });
          setSkillAssessmentLinkModalOpen(true);
        }

        // Generate unified success toast message
        const toastMessage = (() => {
          if (sendInterviewHost && sendSkillAssessment) {
            return `Assessment invitations sent to ${candidateName}. Interview sent now, AI skill assessment will follow automatically.`;
          } else if (sendInterviewHost) {
            return `Interview invitation sent to ${candidateName}`;
          } else if (sendSkillAssessment) {
            return `AI Skill assessment invitation sent to ${candidateName}`;
          }
          return `Assessment invitation sent to ${candidateName}`;
        })();

        showToast(toastMessage);
      } catch (error) {
        console.error('Failed to send assessment invitation:', error);
        const errorMessage =
          typeof error === 'string' ? error : 'Failed to send assessment invitation. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setSendingInterview(false);
      }
    },
    [dispatch, jobId, showToast]
  );

  const handleAssessmentSaved = useCallback(() => {
    showToast('Assessment configuration saved.');
    fetchCandidatesWithFilters();
  }, [showToast, fetchCandidatesWithFilters]);

  const handleInterviewLinkModalClose = useCallback(() => {
    setInterviewLinkModalOpen(false);
    setInterviewLinkData(null);
  }, []);

  const handleRunSkillAssessment = useCallback(
    async (candidateId: string, candidateName: string) => {
      if (!jobId) return;

      setRunningSkillAssessment(true);
      try {
        const result = await dispatch(runSkillAssessment({ candidateId, jobId })).unwrap();
        setSkillAssessmentLinkData({ data: result, candidateName });
        setSkillAssessmentLinkModalOpen(true);
        showToast(`Skill assessment sent to ${candidateName}`);
      } catch (error) {
        showToast(typeof error === 'string' ? error : 'Failed to run skill assessment', 'error');
        console.error('Failed to run skill assessment:', error);
      } finally {
        setRunningSkillAssessment(false);
      }
    },
    [dispatch, jobId, showToast]
  );

  const handleSkillAssessmentLinkModalClose = useCallback(() => {
    setSkillAssessmentLinkModalOpen(false);
    setSkillAssessmentLinkData(null);
  }, []);

  // Auto-fire any Skill Assessment invites the recruiter pre-selected in the
  // Configure Assessment modal, once the candidate's Interview Host round has
  // actually completed (skill_assessment.can_run flips to true / state is
  // 'not_started'). This is a client-side fallback since the backend doesn't
  // currently support an "auto-send after host completes" flag - see
  // pendingSkillAssessments.ts for details.
  const autoFiringRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!jobId || !candidates) return;

    candidates.candidates.forEach((candidate) => {
      const pending = getPendingSkillAssessment(candidate.candidate_id);
      if (!pending) return;
      if (autoFiringRef.current.has(candidate.candidate_id)) return;

      const canRunNow = candidate.skill_assessment?.can_run && candidate.skill_assessment?.state === 'not_started';
      if (!canRunNow) return;

      autoFiringRef.current.add(candidate.candidate_id);

      const deadlineAt = new Date(Date.now() + pending.deadlineHours * 60 * 60 * 1000).toISOString();

      dispatch(runSkillAssessment({ candidateId: candidate.candidate_id, jobId, deadlineAt }))
        .unwrap()
        .then(() => {
          clearPendingSkillAssessment(candidate.candidate_id);
          showToast(`Skill assessment automatically sent to ${pending.candidateName} — their interview is complete.`);
        })
        .catch((error) => {
          console.error('Failed to auto-send queued skill assessment:', error);
          autoFiringRef.current.delete(candidate.candidate_id);
        });
    });
  }, [candidates, jobId, dispatch, showToast]);

  const handleRemindAction = useCallback(
    async (candidateId: string, candidateName: string) => {
      if (!jobId) return;

      try {
        await dispatch(handleInvitationAction({ candidateId, jobId, action: 'remind' })).unwrap();
        showToast(`Reminder sent to ${candidateName}`);
      } catch (error) {
        console.error('Failed to send reminder:', error);
      }
    },
    [dispatch, jobId, showToast]
  );

  const handleCancelClick = useCallback((candidateId: string, candidateName: string) => {
    setConfirmCancelModal({ open: true, candidateId, candidateName });
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    if (!jobId || !confirmCancelModal.candidateId) return;

    setCancellingInvite(true);
    try {
      await dispatch(
        handleInvitationAction({
          candidateId: confirmCancelModal.candidateId,
          jobId,
          action: 'cancel'
        })
      ).unwrap();
      setConfirmCancelModal({
        open: false,
        candidateId: '',
        candidateName: ''
      });
      showToast('Interview invitation cancelled');
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      const errorMessage = typeof error === 'string' ? error : 'Failed to cancel invitation. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setCancellingInvite(false);
    }
  }, [dispatch, jobId, confirmCancelModal.candidateId, showToast]);

  const handleCancelModalClose = useCallback(() => {
    if (!cancellingInvite) {
      setConfirmCancelModal({
        open: false,
        candidateId: '',
        candidateName: ''
      });
    }
  }, [cancellingInvite]);

  const handleExport = useCallback(async () => {
    if (!jobId) return;

    setExporting(true);
    try {
      await dispatch(exportCandidates({ jobId })).unwrap();
      showToast('Candidates exported successfully');
    } catch (error) {
      showToast('Failed to export candidates');
      console.error('Failed to export candidates:', error);
    } finally {
      setExporting(false);
    }
  }, [dispatch, jobId, showToast]);

  const handleDownloadReport = useCallback(
    async (candidate: Candidate) => {
      // Find session_id from candidate data
      // Note: Assuming session_id comes from invite or needs to be fetched
      // For now, using candidate_id as fallback - this may need adjustment based on actual API
      const sessionId = candidate.candidate_id; // Update this based on actual session_id location

      setDownloadingReportId(candidate.candidate_id);
      try {
        const blob = await dispatch(exportInterviewReport({ sessionId })).unwrap();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Interview_Report_${candidate.name.replace(/\s+/g, '_')}_${sessionId}.pdf`;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast('Report downloaded successfully');
      } catch (error) {
        showToast('Failed to download report');
        console.error('Failed to download report:', error);
      } finally {
        setDownloadingReportId(null);
      }
    },
    [dispatch, showToast]
  );

  const handleViewSkillAssessmentReport = useCallback(
    (candidateId: string, jobId: string) => {
      navigate(`/jobs/${jobId}/candidates/${candidateId}/skill-assessment-report`);
    },
    [navigate]
  );

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ScreeningInProgress':
        return styles.statusScreeningInProgress;
      case 'ScreeningCompleted':
        return styles.statusScreeningCompleted;
      case 'Invited':
        return styles.statusInvited;
      case 'ReportReady':
        return styles.statusReportReady;
      case 'Decided':
        return styles.statusDecided;
      default:
        return styles.statusDefault;
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusLabel = useCallback((status: string): string => {
    const statusLabels: Record<string, string> = {
      All: 'All',
      ScreeningInProgress: 'Screening in progress',
      ScreeningCompleted: 'Screening completed',
      Invited: 'Invited',
      ReportReady: 'Report ready',
      Decided: 'Decided'
    };
    return statusLabels[status] || status;
  }, []);

  const getAvatarColor = (index: number): string => {
    const colors = [styles.avatarBlue, styles.avatarOrange, styles.avatarTeal, styles.avatarPurple];
    return colors[index % colors.length];
  };

  const getExpirationStatus = useCallback((expiresAt: string) => {
    const hoursRemaining = Math.floor((new Date(expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60));

    if (hoursRemaining <= 0) {
      const hoursAgo = Math.abs(hoursRemaining);
      return { text: `expired ${hoursAgo} hrs ago`, isExpired: true };
    }

    return { text: `expires in ${hoursRemaining} hrs`, isExpired: false };
  }, []);

  const getAppliedText = useCallback((daysAgo: number): string => {
    if (daysAgo === 0) return 'applied today';
    if (daysAgo === 1) return 'applied 1 day ago';
    return `applied ${daysAgo} days ago`;
  }, []);

  // Parse skills text - handle semicolon-separated lists
  const parseSkillsText = useCallback((text: string): string[] => {
    if (!text || typeof text !== 'string') return [];

    return text
      .split(';')
      .map((skill) => skill.trim())
      .filter((skill) => skill.length > 0);
  }, []);

  // Type guard to check if screening_output is the new format
  const isNewScreeningFormat = useCallback(
    (
      output: any
    ): output is {
      skills: { functional: Array<{ text: string; type: string }>; preferred: Array<{ text: string; type: string }> };
    } => {
      return (
        output &&
        typeof output === 'object' &&
        'skills' in output &&
        Array.isArray(output.skills?.functional) &&
        Array.isArray(output.skills?.preferred)
      );
    },
    []
  );

  const selectedCandidateConfig = useMemo(() => {
    if (!selectedCandidateForConfig || !candidates) return null;
    const candidate = candidates.candidates.find((c) => c.candidate_id === selectedCandidateForConfig);
    const config = candidate?.interview_configuration;
    return {
      candidateId: selectedCandidateForConfig,
      candidate,
      interviewConfig: {
        duration_minutes: config?.duration_minutes ?? 25,
        language: config?.language || 'English',
        mandatory_questions: config?.mandatory_questions ?? 2,
        focus_areas: config?.focus_areas || [],
        off_limit_topics: config?.off_limit_topics || [],
        delivery_channels: config?.delivery_channels?.length
          ? config.delivery_channels
          : (['interview_host'] as AssessmentDeliveryChannel[]),
        question_level: config?.question_level
      },
      interviewContext: candidate?.interview_context || ''
    };
  }, [selectedCandidateForConfig, candidates]);

  // Most recently added/applied candidates first
  const sortedCandidates = useMemo(() => {
    if (!candidates) return [];
    return [...candidates.candidates].sort(
      (a, b) => new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime()
    );
  }, [candidates]);

  if (candidatesLoading) {
    return <PageLoader label="Loading candidates..." activeNav="home" showSidebar={true} />;
  }

  if (candidatesError || !candidates) {
    return (
      <div className={styles.container}>
        <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} />
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load candidates</h2>
            <p>{candidatesError || 'An unexpected error occurred.'}</p>
            <button onClick={fetchCandidatesWithFilters}>Try Again</button>
          </div>
        </main>
      </div>
    );
  }

  const { job, role_context, status_counts } = candidates;

  return (
    <div className={styles.container}>
      <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} />
      <main className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button className={styles.breadcrumbLink} onClick={handleBackToJobs}>
            Jobs
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>{job.title}</span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Candidates</h1>
            <p className={styles.subtitle}>
              {job.title} · {job.department} · {job.location}
            </p>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.btnRefresh} onClick={fetchCandidatesWithFilters} disabled={candidatesLoading}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Refresh
            </button>
            <button className={styles.btnAddCandidate} onClick={() => navigate(`/jobs/${jobId}/candidates/add`)}>
              <AddIcon sx={{ fontSize: 18 }} />
              Add candidate
            </button>
          </div>
        </div>

        {/* Role Context */}
        {role_context?.text && (
          <div className={styles.roleContextCard}>
            <div className={styles.roleContextHeader}>
              <div className={styles.roleContextIcon}>⚙️</div>
              <div className={styles.roleContextTitle}>Role context</div>
            </div>
            <p className={styles.roleContextText}>{role_context.text}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className={styles.searchSection}>
          <div className={styles.searchBox}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by name or candidate ID..."
              value={searchInput}
              onChange={handleSearchChange}
            />
            {searchInput && (
              <button className={styles.searchClear} onClick={handleClearSearch} aria-label="Clear search">
                <ClearIcon sx={{ fontSize: 18 }} />
              </button>
            )}
          </div>
          <div className={styles.searchActions}>
            <button className={styles.btnIcon} onClick={() => showToast('Filter functionality coming soon')}>
              <FilterListIcon sx={{ fontSize: 20 }} />
              Filter
            </button>
            <button className={styles.btnIcon} onClick={handleExport} disabled={exporting}>
              <FileDownloadIcon sx={{ fontSize: 20 }} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className={styles.statusTabs}>
          {(Object.keys(status_counts) as StatusFilter[]).map((status) => (
            <button
              key={status}
              className={`${styles.statusTab} ${activeStatus === status ? styles.statusTabActive : ''}`}
              onClick={() => handleStatusChange(status)}
            >
              {getStatusLabel(status)}
              <span className={styles.statusCount}>{status_counts[status]}</span>
            </button>
          ))}
        </div>

        {/* Loading Overlay for Filtering */}
        {candidatesLoading && candidates && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>
              <RefreshIcon className={styles.spinIcon} />
              <span>Loading...</span>
            </div>
          </div>
        )}

        {/* Candidates List */}
        <div className={styles.candidatesList}>
          {sortedCandidates.length === 0 ? (
            <div className={styles.emptyState}>
              <SearchIcon sx={{ fontSize: 48, color: '#adb5bd' }} />
              <h3>No candidates found</h3>
              <p>
                {searchInput
                  ? `No results for "${searchInput}"`
                  : activeStatus === 'All'
                    ? 'No candidates available for this job'
                    : `No candidates with status "${getStatusLabel(activeStatus)}"`}
              </p>
              {(searchInput || activeStatus !== 'All') && (
                <button className={styles.btnSecondary} onClick={handleClearAllFilters}>
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            sortedCandidates.map((candidate, index) => (
              <div key={candidate.candidate_id} className={styles.candidateCard}>
                {/* Candidate Header - Always Visible */}
                <div
                  className={styles.candidateHeader}
                  onClick={() => toggleCandidateExpanded(candidate.candidate_id, candidate.status)}
                  style={{
                    cursor: ['ScreeningCompleted', 'Invited', 'ReportReady', 'Decided'].includes(candidate.status)
                      ? 'pointer'
                      : 'default'
                  }}
                >
                  <div className={styles.candidateLeft}>
                    <div className={`${styles.avatar} ${getAvatarColor(index)}`}>{getInitials(candidate.name)}</div>
                    <div className={styles.candidateInfo}>
                      <h3 className={styles.candidateName}>{candidate.name}</h3>
                      <p className={styles.candidateMeta}>
                        {candidate.candidate_id} ·{' '}
                        {getAppliedText(
                          candidate.applied_at
                            ? Math.floor(
                                (Date.now() - new Date(candidate.applied_at).getTime()) / (1000 * 60 * 60 * 24)
                              )
                            : 0
                        )}
                      </p>
                      {candidate.jd_match > 90 && candidate.is_ai_detected && (
                        <span className={styles.aiDetectedBadge}>AI Detected</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.candidateRight}>
                    {/* Interview Score */}
                    {candidate.status === 'ReportReady' &&
                      candidate.interview_score !== null &&
                      candidate.interview_score !== undefined && (
                        <div className={styles.matchScore}>
                          <span className={styles.matchPercentage}>{candidate.interview_score}%</span>
                          <span className={styles.matchLabel}>Interview score</span>
                        </div>
                      )}

                    {/* Skill Assessment Score */}
                    {candidate.status === 'ReportReady' &&
                      candidate.skill_score !== null &&
                      candidate.skill_score !== undefined && (
                        <div className={styles.matchScore}>
                          <span className={styles.matchPercentage}>{candidate.skill_score}%</span>
                          <span className={styles.matchLabel}>Skill score</span>
                        </div>
                      )}

                    {/* JD Match */}
                    {candidate.jd_match !== null && candidate.jd_match !== undefined ? (
                      <>
                        <div className={styles.matchScore}>
                          <span className={styles.matchPercentage}>{candidate.jd_match}%</span>
                          <span className={styles.matchLabel}>JD match</span>
                        </div>
                        <div className={styles.matchBar}>
                          <div className={styles.matchBarFill} style={{ width: `${candidate.jd_match}%` }} />
                        </div>
                      </>
                    ) : (
                      <div className={styles.matchScorePlaceholder} />
                    )}
                    <div className={`${styles.statusBadge} ${getStatusColor(candidate.status)}`}>
                      {getStatusLabel(candidate.status)}
                    </div>
                    <div className={styles.expandIconWrapper}>
                      {['ScreeningCompleted', 'Invited', 'ReportReady', 'Decided'].includes(candidate.status) &&
                        (expandedCandidateId === candidate.candidate_id ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCandidateId === candidate.candidate_id && (
                  <div className={styles.candidateExpanded}>
                    {/* For ScreeningCompleted Status */}
                    {candidate.status === 'ScreeningCompleted' && (
                      <>
                        <div className={styles.expandedRow}>
                          <div className={styles.expandedCol}>
                            <div className={styles.sectionTitle}>
                              <span className={styles.sectionIcon}>📋</span>
                              SCREENING AGENT OUTPUT
                            </div>
                            <div className={styles.screeningTable}>
                              {(() => {
                                const screeningOutput = candidate.screening_output;
                                if (screeningOutput && isNewScreeningFormat(screeningOutput)) {
                                  return (
                                    <div className={styles.accordionContainer}>
                                      {/* Functional/Technical Skills Section */}
                                      <div className={styles.accordionItem}>
                                        <button
                                          className={styles.accordionHeader}
                                          onClick={() => toggleAccordion(candidate.candidate_id, 'functional')}
                                          aria-expanded={expandedAccordions[`${candidate.candidate_id}_functional`]}
                                        >
                                          <div className={styles.accordionTitle}>
                                            <span className={styles.accordionSectionTitle}>
                                              Functional/Technical Skills
                                            </span>
                                          </div>
                                          {expandedAccordions[`${candidate.candidate_id}_functional`] ? (
                                            <KeyboardArrowUpIcon className={styles.accordionChevron} />
                                          ) : (
                                            <KeyboardArrowDownIcon className={styles.accordionChevron} />
                                          )}
                                        </button>
                                        {expandedAccordions[`${candidate.candidate_id}_functional`] && (
                                          <div className={styles.accordionContent}>
                                            {screeningOutput.skills.functional &&
                                            screeningOutput.skills.functional.length > 0 ? (
                                              <div className={styles.skillsList}>
                                                {screeningOutput.skills.functional.map((item, idx) => {
                                                  const skillPoints = parseSkillsText(item.text);
                                                  return skillPoints.length > 1 ? (
                                                    // Multiple skills - render as bullet list
                                                    <div
                                                      key={idx}
                                                      className={`${styles.skillItem} ${
                                                        item.type === 'strength'
                                                          ? styles.skillItemStrength
                                                          : styles.skillItemGap
                                                      }`}
                                                    >
                                                      {item.type === 'strength' ? (
                                                        <CheckCircleIcon className={styles.skillIcon} />
                                                      ) : (
                                                        <ClearIcon className={styles.skillIcon} />
                                                      )}
                                                      <div className={styles.skillTextList}>
                                                        {skillPoints.map((skill, skillIdx) => (
                                                          <div key={skillIdx} className={styles.skillPoint}>
                                                            {skill}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    // Single skill - render normally
                                                    <div
                                                      key={idx}
                                                      className={`${styles.skillItem} ${
                                                        item.type === 'strength'
                                                          ? styles.skillItemStrength
                                                          : styles.skillItemGap
                                                      }`}
                                                    >
                                                      {item.type === 'strength' ? (
                                                        <CheckCircleIcon className={styles.skillIcon} />
                                                      ) : (
                                                        <ClearIcon className={styles.skillIcon} />
                                                      )}
                                                      <span className={styles.skillText}>{item.text}</span>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <p className={styles.accordionTextEmpty}>No information available</p>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Preferred Qualifications Section */}
                                      <div className={styles.accordionItem}>
                                        <button
                                          className={styles.accordionHeader}
                                          onClick={() => toggleAccordion(candidate.candidate_id, 'preferred')}
                                          aria-expanded={expandedAccordions[`${candidate.candidate_id}_preferred`]}
                                        >
                                          <div className={styles.accordionTitle}>
                                            <span className={styles.accordionSectionTitle}>
                                              Preferred Qualifications
                                            </span>
                                          </div>
                                          {expandedAccordions[`${candidate.candidate_id}_preferred`] ? (
                                            <KeyboardArrowUpIcon className={styles.accordionChevron} />
                                          ) : (
                                            <KeyboardArrowDownIcon className={styles.accordionChevron} />
                                          )}
                                        </button>
                                        {expandedAccordions[`${candidate.candidate_id}_preferred`] && (
                                          <div className={styles.accordionContent}>
                                            {screeningOutput.skills.preferred &&
                                            screeningOutput.skills.preferred.length > 0 ? (
                                              <div className={styles.skillsList}>
                                                {screeningOutput.skills.preferred.map((item, idx) => {
                                                  const skillPoints = parseSkillsText(item.text);
                                                  return skillPoints.length > 1 ? (
                                                    // Multiple skills - render as bullet list
                                                    <div
                                                      key={idx}
                                                      className={`${styles.skillItem} ${
                                                        item.type === 'strength'
                                                          ? styles.skillItemStrength
                                                          : styles.skillItemGap
                                                      }`}
                                                    >
                                                      {item.type === 'strength' ? (
                                                        <CheckCircleIcon className={styles.skillIcon} />
                                                      ) : (
                                                        <ClearIcon className={styles.skillIcon} />
                                                      )}
                                                      <div className={styles.skillTextList}>
                                                        {skillPoints.map((skill, skillIdx) => (
                                                          <div key={skillIdx} className={styles.skillPoint}>
                                                            {skill}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    // Single skill - render normally
                                                    <div
                                                      key={idx}
                                                      className={`${styles.skillItem} ${
                                                        item.type === 'strength'
                                                          ? styles.skillItemStrength
                                                          : styles.skillItemGap
                                                      }`}
                                                    >
                                                      {item.type === 'strength' ? (
                                                        <CheckCircleIcon className={styles.skillIcon} />
                                                      ) : (
                                                        <ClearIcon className={styles.skillIcon} />
                                                      )}
                                                      <span className={styles.skillText}>{item.text}</span>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <p className={styles.accordionTextEmpty}>No information available</p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                return <p className={styles.noDataText}>No screening output available</p>;
                              })()}
                            </div>
                          </div>

                          <div className={styles.expandedCol}>
                            <div className={styles.sectionTitle}>
                              <span className={styles.sectionIcon}>💬</span>
                              INTERVIEW CONTEXT
                            </div>
                            <div className={styles.interviewContext}>
                              <p>{candidate.interview_context || 'No context provided'}</p>
                            </div>
                            {candidate.interview_configuration && (
                              <div className={styles.interviewConfig}>
                                {Number.isFinite(candidate.experience_years) && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>💼</span>
                                    {candidate.experience_years} {candidate.experience_years === 1 ? 'yr' : 'yrs'} exp
                                  </div>
                                )}
                                {candidate.interview_configuration.duration_minutes && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>⏱️</span>
                                    {candidate.interview_configuration.duration_minutes} min
                                  </div>
                                )}
                                {candidate.interview_configuration.language && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>🌐</span>
                                    {candidate.interview_configuration.language}
                                  </div>
                                )}
                                {/* {candidate.interview_configuration.mandatory_questions !== undefined && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>⭐</span>
                                    {candidate.interview_configuration.mandatory_questions} mandatory Qs
                                  </div>
                                )} */}
                                {candidate.interview_configuration.question_level && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>📈</span>
                                    {candidate.interview_configuration.question_level.charAt(0).toUpperCase() +
                                      candidate.interview_configuration.question_level.slice(1)}{' '}
                                    level
                                  </div>
                                )}
                                {candidate.interview_configuration.focus_areas && (
                                  <div className={styles.configItem}>
                                    <span className={styles.configIcon}>🎯</span>
                                    {candidate.interview_configuration.focus_areas.length} focus areas
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className={styles.candidateActions}>
                          {/* Temporarily disabled - View screening report */}
                          {/* <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(
                                candidate.screening.report_url,
                                "_blank",
                              )
                            }
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                            View screening report
                          </button> */}
                          <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(
                                candidate.resume?.url || `/interview-host/candidates/${candidate.candidate_id}/cv`,
                                '_blank'
                              )
                            }
                          >
                            <DescriptionIcon sx={{ fontSize: 16 }} />
                            View CV
                          </button>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            onClick={() => handleConfigureClick(candidate.candidate_id)}
                          >
                            Configure
                          </button>
                          <button
                            className={styles.btnReject}
                            onClick={() => handleRejectClick(candidate.candidate_id, candidate.name)}
                          >
                            Reject
                          </button>
                          <button
                            className={styles.btnPrimary}
                            onClick={() => handleSendInterviewLink(candidate)}
                            disabled={sendingInterview}
                          >
                            {sendingInterview ? 'Sending...' : 'Send link'}
                          </button>
                        </div>
                      </>
                    )}

                    {/* For Invited Status */}
                    {candidate.status === 'Invited' && (
                      <>
                        <div className={styles.inviteStatus}>
                          <span className={styles.inviteIcon}>ℹ️</span>
                          {candidate.invite ? (
                            <>
                              Link sent{' '}
                              {Math.floor(
                                (new Date().getTime() - new Date(candidate.invite.sent_at).getTime()) / (1000 * 60 * 60)
                              )}{' '}
                              hrs ago · {candidate.invite.opened_at ? 'opened' : 'not yet opened'} ·{' '}
                              {getExpirationStatus(candidate.invite.expires_at).text}
                            </>
                          ) : (
                            'Invite details are being processed'
                          )}
                        </div>
                        <div className={styles.candidateActions}>
                          <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(
                                candidate.resume?.url || `/interview-host/candidates/${candidate.candidate_id}/cv`,
                                '_blank'
                              )
                            }
                          >
                            <DescriptionIcon sx={{ fontSize: 16 }} />
                            View CV
                          </button>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            disabled={!candidate.invite}
                            onClick={() => handleRemindAction(candidate.candidate_id, candidate.name)}
                          >
                            <span>🔔</span> Send reminder
                          </button>
                          <button
                            className={styles.btnReject}
                            disabled={!candidate.invite}
                            onClick={() => handleCancelClick(candidate.candidate_id, candidate.name)}
                          >
                            Cancel invite
                          </button>
                        </div>
                      </>
                    )}

                    {/* For Report Ready Status */}
                    {candidate.status === 'ReportReady' && (
                      <>
                        <div className={styles.reportSummaryInfo}>
                          {candidate.overall_score !== null && candidate.overall_score !== undefined && (
                            <>
                              <strong>{candidate.overall_score}/100</strong> overall
                            </>
                          )}
                          {candidate.report_summary?.recommendation && (
                            <>
                              {' · '}
                              <strong style={{ textTransform: 'capitalize' }}>
                                {candidate.report_summary.recommendation}
                              </strong>{' '}
                              recommended
                            </>
                          )}
                          {candidate.report_summary?.otp_verified && <> · OTP verified</>}
                          {candidate.report_summary?.person_count !== undefined && (
                            <> · {candidate.report_summary.person_count} person</>
                          )}
                          {candidate.report_summary?.tab_switches !== undefined &&
                            candidate.report_summary.tab_switches > 0 && (
                              <> · {candidate.report_summary.tab_switches} tab switches</>
                            )}
                        </div>
                        <div className={styles.candidateActions}>
                          <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(
                                candidate.resume?.url || `/interview-host/candidates/${candidate.candidate_id}/cv`,
                                '_blank'
                              )
                            }
                          >
                            <DescriptionIcon sx={{ fontSize: 16 }} />
                            View CV
                          </button>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            onClick={() => handleDownloadReport(candidate)}
                            disabled={downloadingReportId === candidate.candidate_id}
                          >
                            <FileDownloadIcon sx={{ fontSize: 16 }} />
                            {downloadingReportId === candidate.candidate_id ? 'Downloading...' : 'Download'}
                          </button>
                          <button
                            className={styles.btnPrimary}
                            onClick={() => navigate(`/jobs/${jobId}/candidates/${candidate.candidate_id}/report`)}
                          >
                            Review report
                          </button>
                        </div>
                      </>
                    )}

                    {/* For Decided Status */}
                    {candidate.status === 'Decided' && (
                      <>
                        <div className={styles.decisionInfo}>
                          Decision: <strong>{candidate.decision?.outcome || 'Pending'}</strong>
                          {candidate.decision && (
                            <>
                              {' '}
                              · by {candidate.decision.decided_by} on{' '}
                              {new Date(candidate.decision.decided_at).toLocaleDateString()}
                            </>
                          )}
                        </div>
                        <div className={styles.candidateActions}>
                          <button
                            className={styles.btnLink}
                            onClick={() =>
                              window.open(
                                candidate.resume?.url || `/interview-host/candidates/${candidate.candidate_id}/cv`,
                                '_blank'
                              )
                            }
                          >
                            <DescriptionIcon sx={{ fontSize: 16 }} />
                            View CV
                          </button>
                          <div className={styles.actionsSpacer} />
                          <button
                            className={styles.btnSecondary}
                            onClick={() => navigate(`/jobs/${jobId}/candidates/${candidate.candidate_id}/report`)}
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                            View report
                          </button>
                          <button
                            className={styles.btnSecondary}
                            disabled={!candidate.decision}
                            onClick={() => navigate(`/jobs/${jobId}/candidates/${candidate.candidate_id}/report`)}
                          >
                            <SettingsIcon sx={{ fontSize: 16 }} />
                            Change decision
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {/* Modals */}
      {configureAssessmentModalOpen && selectedCandidateConfig && jobId && (
        <ConfigureAssessmentModal
          open={configureAssessmentModalOpen}
          onClose={handleConfigModalClose}
          candidateId={selectedCandidateConfig.candidateId}
          jobId={jobId}
          candidateName={selectedCandidateConfig.candidate?.name || 'this candidate'}
          roleTitle={job.title}
          currentConfig={selectedCandidateConfig.interviewConfig}
          currentContext={selectedCandidateConfig.interviewContext}
          onSaved={handleAssessmentSaved}
          onShowToast={showToast}
        />
      )}

      {rejectModalOpen && selectedCandidateForReject && jobId && (
        <RejectCandidateModal
          open={rejectModalOpen}
          onClose={handleRejectModalClose}
          candidateId={selectedCandidateForReject.id}
          candidateName={selectedCandidateForReject.name}
          jobId={jobId}
        />
      )}

      {interviewLinkModalOpen && interviewLinkData && (
        <InterviewLinkModal
          open={interviewLinkModalOpen}
          onClose={handleInterviewLinkModalClose}
          interviewData={interviewLinkData}
        />
      )}

      {skillAssessmentLinkModalOpen && skillAssessmentLinkData && (
        <SkillAssessmentLinkModal
          open={skillAssessmentLinkModalOpen}
          onClose={handleSkillAssessmentLinkModalClose}
          assessmentData={skillAssessmentLinkData.data}
          candidateName={skillAssessmentLinkData.candidateName}
        />
      )}

      {confirmCancelModal.open && (
        <ConfirmCancelModal
          open={confirmCancelModal.open}
          onClose={handleCancelModalClose}
          onConfirm={handleCancelConfirm}
          candidateName={confirmCancelModal.candidateName}
          loading={cancellingInvite}
        />
      )}

      {/* Toast Notification - Bottom Right */}
      {toast.open && (
        <div className={styles.toastContainer}>
          <div className={styles.toast}>
            {toast.type === 'error' ? (
              <WarningIcon className={styles.toastIcon} />
            ) : (
              <CheckCircleIcon className={styles.toastIcon} />
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidates;
